/**
 * flicker — anchored glows whose alpha shimmers (torch / heat-haze / caustic
 * light). Each anchor gets one `glow_soft` sprite parked at its world position;
 * a single overlay-ticker subscription drives every sprite's alpha each frame
 * via the seeded shimmer curve (flickerConfig.flickerAlpha). Unlike the
 * tween-driven effects (fireflies/softLights), the shimmer is pseudo-random so
 * it needs a per-frame tick — modelled on weatherEffect's addTick usage.
 * Under reduced motion there is no tick: each glow holds the base alpha.
 */
import { registerEffect, makeContentHandle, type EffectBuilder, type EffectHandle } from '../effectBuilders';
import { flickerAlpha, placeFlickers, anchorFlickersToSurface } from '../../vfx/flickerConfig';
import { regionTopY } from './weatherConfig';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import type { ISprite } from '../../../rendering/IRenderer';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';

const buildFlicker: EffectBuilder = (layer, ctx): EffectHandle | null => {
  if (layer.kind !== 'flicker') return null;

  // Volcanic heat-haze: when the level authors a 'lava' region, snap the glow
  // bases onto its surface top so the shimmer rides the lava line (T7).
  const placed = anchorFlickersToSurface(
    placeFlickers(layer.anchors, ctx.region, layer.hzMin, layer.hzMax, ctx.seed),
    regionTopY(ctx.scene.regions, 'lava'),
  );
  const sprites: ISprite[] = [];
  // Per-sprite shimmer params, index-aligned with `sprites`.
  const params: { hz: number; seed: number }[] = [];

  for (const p of placed) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const s = createGlowSprite({ x: p.x, y: p.y, size: layer.size, color: layer.color, alpha: layer.baseAlpha, parent: ctx.layerContainer });
    if (!s) continue; // glow texture not ready yet — skip this anchor, keep the rest
    sprites.push(s);
    params.push({ hz: p.hz, seed: p.seed });
  }

  // Reduced motion: the glows keep the base alpha they were made with.
  if (getMotionPrefs().reduceMotion) return makeContentHandle({ sprites });

  let elapsedMs = 0;
  // ONE tick subscription drives every glow's alpha. addTick returns its own
  // unsubscribe — the handle's destroy() MUST call it (else the closure leaks).
  const unsubscribe = ctx.addTick((dtMs: number) => {
    elapsedMs += dtMs;
    for (let i = 0; i < sprites.length; i += 1) {
      const { hz, seed } = params[i];
      sprites[i].setAlpha(flickerAlpha(elapsedMs, hz, layer.baseAlpha, layer.flickerAmp, seed));
    }
  });

  return makeContentHandle({ sprites, onDestroy: unsubscribe });
};

registerEffect({ kind: 'flicker', build: buildFlicker, depth: Z_LAYERS.ATMOSPHERE });
